// SPDX-License-Identifier: AGPL-3.0-or-later

import MessagesState from '@app/features/messaging/state/MessagingMessages';
import * as ReadStateCommands from '@app/features/read_state/commands/ReadStateCommands';
import ReadStates from '@app/features/read_state/state/ReadStates';
import {compareMessageIds} from '@app/features/read_state/state/read_states/shared';
import UnreadCatchUpSettings from '@app/features/read_state/state/UnreadCatchUpSettings';
import {
	CHANNEL_LEAVE_CLEAR_MIN_VISIT_MS,
	computeAtLiveBottom,
	hasOffScreenRemainingUnread,
	isAllUnreadVisible,
	isKeyboardScrollIntentKey,
	isLastMessageTextVisibleForLeave,
	lastMessageTextVisibleNeedsBounds,
	offScreenRemainingUnreadNeedsBounds,
	resolveScrollAckMessageId,
	shouldClearUnreadOnVisibleBottomTrigger,
	shouldCompleteCatchUpOnBottomOverscroll,
	shouldHideNewBannerOnScrollStart,
	shouldShowTopNewMessagesBar,
	VISIBLE_BOTTOM_IDLE_CLEAR_MS,
	type VisibleBottomClearTrigger,
} from '@app/features/read_state/utils/UnreadCatchUp';
import {CatchUpBottomClocks} from '@app/features/read_state/utils/UnreadCatchUpClocks';
import Window from '@app/features/window/state/Window';
import debounce from 'lodash/debounce';

export const CATCH_UP_SCROLL_DEBOUNCE_MS = 100;

export interface UnreadCatchUpHost {
	getScroller(): HTMLElement | null;
	getInner(): HTMLElement | null;
	isJumpActive(): boolean;
	isTailGapPending(): boolean;
	onRemainingUnread(hasOffScreenRemainingUnread: boolean): void;
}

type MeasuredMessage = {id: string; top: number; bottom: number};

const MESSAGE_NODE_SELECTOR = '[role=article][data-message-id]';
const NO_MEASURED_MESSAGES: ReadonlyArray<MeasuredMessage> = [];

function measureMessageBounds(inner: HTMLElement): Array<MeasuredMessage> {
	const measured: Array<MeasuredMessage> = [];
	for (const node of inner.querySelectorAll<HTMLElement>(MESSAGE_NODE_SELECTOR)) {
		const id = node.dataset.messageId;
		if (id == null) {
			continue;
		}
		const rect = node.getBoundingClientRect();
		measured.push({id, top: rect.top, bottom: rect.bottom});
	}
	return measured;
}

/**
 * Same result as the last entry of `measureMessageBounds`, without laying out the rest.
 * The scroll path only needs the bottom row, and forcing a rect for every loaded message
 * on each scroll event is the dominant cost in a full 200-message window.
 */
function measureLastMessageBounds(inner: HTMLElement): MeasuredMessage | null {
	const nodes = inner.querySelectorAll<HTMLElement>(MESSAGE_NODE_SELECTOR);
	for (let index = nodes.length - 1; index >= 0; index--) {
		const node = nodes[index];
		const id = node.dataset.messageId;
		if (id == null) {
			continue;
		}
		const rect = node.getBoundingClientRect();
		return {id, top: rect.top, bottom: rect.bottom};
	}
	return null;
}

export class UnreadCatchUpController {
	private channelId: string | null = null;
	private visitEnteredAt = 0;
	private lastScrollTop: number | null = null;
	private scrollIntentAtBottom = false;
	private unreadTextsVisibleForLeave = false;
	private idleClearTimer: number | null = null;
	private readonly applyFromScrollDebounced: ReturnType<typeof debounce<() => void>>;
	private readonly clocks: CatchUpBottomClocks;

	constructor(private readonly host: UnreadCatchUpHost) {
		this.clocks = new CatchUpBottomClocks(
			() => {
				const scroller = this.host.getScroller();
				return scroller != null && computeAtLiveBottom(scroller);
			},
			() => {
				this.scheduleIdleClear();
			},
		);
		this.applyFromScrollDebounced = debounce(() => {
			this.applyFromScroll();
		}, CATCH_UP_SCROLL_DEBOUNCE_MS);
	}

	attach(channelId: string): void {
		this.detach();
		this.channelId = channelId;
		this.visitEnteredAt = Date.now();
		this.lastScrollTop = null;
		this.scrollIntentAtBottom = false;
		this.unreadTextsVisibleForLeave = false;
		this.clocks.reset();
		this.resetIdleClear();
		UnreadCatchUpSettings.enterChannelVisit(
			channelId,
			ReadStates.getVisualUnreadMessageId(channelId) ?? ReadStates.getOldestUnreadMessageId(channelId),
			ReadStates.ackMessageId(channelId),
		);
	}

	detach(): void {
		const leavingChannelId = this.channelId;
		this.applyFromScrollDebounced.cancel();
		this.resetIdleClear();
		this.clocks.dispose();
		if (leavingChannelId == null) {
			return;
		}
		if (this.shouldClearOnLeave(leavingChannelId)) {
			ReadStateCommands.completeCatchUpFromBottom(leavingChannelId);
		}
		ReadStateCommands.clearManualAck(leavingChannelId);
		UnreadCatchUpSettings.leaveChannelVisit(leavingChannelId);
		this.channelId = null;
	}

	captureVisitAnchor(
		visualUnreadMessageId: string | null,
		oldestUnreadMessageId: string | null,
		ackMessageId: string | null,
	): void {
		if (this.channelId == null) {
			return;
		}
		UnreadCatchUpSettings.captureVisitAnchor(
			this.channelId,
			visualUnreadMessageId ?? oldestUnreadMessageId,
			ackMessageId,
		);
	}

	onScroll(): void {
		const scroller = this.host.getScroller();
		if (scroller != null) {
			this.clocks.updateFromScroller();
		}
		this.updateLastMessageTextVisibleForLeave();
		this.applyFromScrollDebounced();
	}

	onUserScrollGesture(): void {
		this.tryHideBannerOnScrollStart();
		const channelId = this.channelId;
		if (channelId == null || !ReadStates.hasUnread(channelId)) {
			return;
		}
		this.handleBottomScrollIntent();
	}

	onScrollerKeyDown(key: string): void {
		if (!isKeyboardScrollIntentKey(key)) {
			return;
		}
		this.onUserScrollGesture();
	}

	onTextareaFocus(): void {
		this.resetIdleClear();
		this.tryClearOnVisibleBottomTrigger('textarea_focus');
	}

	onLayoutChanged(): void {
		const channelId = this.channelId;
		if (channelId == null || !MessagesState.getMessages(channelId).ready) {
			this.lastScrollTop = null;
			return;
		}
		this.measureRemainingUnread();
		this.updateLastMessageTextVisibleForLeave();
		const scroller = this.host.getScroller();
		if (scroller != null && computeAtLiveBottom(scroller)) {
			this.clocks.updateFromScroller();
		}
	}

	onIdleToggle(enabled: boolean): void {
		if (enabled) {
			this.scheduleIdleClear();
			return;
		}
		this.resetIdleClear();
	}

	private shouldClearOnLeave(channelId: string): boolean {
		const readState = ReadStates.getIfExists(channelId);
		return shouldClearUnreadOnVisibleBottomTrigger({
			settledAtBottom: false,
			allUnreadVisible: this.unreadTextsVisibleForLeave,
			hasUnread: ReadStates.hasUnread(channelId),
			manualHold: readState?.ackedManually ?? false,
			trigger: 'leave_channel',
			clearOnIdleAtBottom: UnreadCatchUpSettings.clearOnIdleAtBottom,
			clearOnLeaveAtBottom: UnreadCatchUpSettings.clearOnLeaveAtBottom,
			visitDurationMs: Math.max(0, Date.now() - this.visitEnteredAt),
			minVisitDurationMs: CHANNEL_LEAVE_CLEAR_MIN_VISIT_MS,
			unreadTextsVisible: this.unreadTextsVisibleForLeave,
			tailGapPending: this.host.isTailGapPending(),
		});
	}

	private handleBottomScrollIntent(): void {
		if (!this.clocks.noteUserGesture()) {
			return;
		}
		this.resetIdleClear();
		this.scrollIntentAtBottom = true;
		this.applyFromScroll();
		this.clocks.clearParkedBeforeGesture();
	}

	private applyFromScroll(): void {
		const channelId = this.channelId;
		const scroller = this.host.getScroller();
		const inner = this.host.getInner();
		if (channelId == null || scroller == null || inner == null) {
			return;
		}
		if (this.host.isJumpActive()) {
			this.lastScrollTop = null;
			this.scrollIntentAtBottom = false;
			this.clocks.interruptParked();
			return;
		}
		const scrollTop = scroller.scrollTop;
		const previousScrollTop = this.lastScrollTop;
		this.lastScrollTop = scrollTop;
		if (previousScrollTop != null && scrollTop !== previousScrollTop) {
			this.resetIdleClear();
		}
		const scrollingDown = previousScrollTop != null && scrollTop > previousScrollTop + 1;
		const settings = UnreadCatchUpSettings;
		const viewport = scroller.getBoundingClientRect();
		const ackMessageId = ReadStates.ackMessageId(channelId);
		// Measured at most once, and only for the branches that actually read it.
		let measured: ReadonlyArray<MeasuredMessage> | null = null;
		const measureAll = (): ReadonlyArray<MeasuredMessage> => {
			measured ??= measureMessageBounds(inner);
			return measured;
		};
		const hasUnread = ReadStates.hasUnread(channelId);
		const hasMoreAfterNow = MessagesState.getMessages(channelId).hasMoreAfter;
		const offScreenRemainingUnread = hasOffScreenRemainingUnread({
			hasUnread,
			hasMoreAfter: hasMoreAfterNow,
			unreadMessageBounds: offScreenRemainingUnreadNeedsBounds({hasUnread, hasMoreAfter: hasMoreAfterNow})
				? this.collectUnreadBounds(inner, channelId, measureAll())
				: NO_MEASURED_MESSAGES,
			viewportTop: viewport.top,
			viewportBottom: viewport.bottom,
		});
		this.host.onRemainingUnread(offScreenRemainingUnread);
		this.updateLastMessageTextVisibleForLeave(measured ?? undefined, viewport);
		const manualHold = ReadStates.getIfExists(channelId)?.ackedManually ?? false;
		const messages = MessagesState.getMessages(channelId);
		const scrollIntentAtBottom = this.scrollIntentAtBottom;
		this.scrollIntentAtBottom = false;
		const parkedBeforeGesture = this.clocks.parkedBeforeGesture;
		const allUnreadVisible = isAllUnreadVisible({
			hasUnread: ReadStates.hasUnread(channelId),
			hasOffScreenRemainingUnread: offScreenRemainingUnread,
		});
		if (
			shouldCompleteCatchUpOnBottomOverscroll({
				clearDividerAtBottom: settings.clearDividerAtBottom,
				messagesReady: messages.ready,
				hasMoreAfter: messages.hasMoreAfter,
				scrollIntentAtBottom,
				settledAtBottom: this.clocks.settledAtBottom,
				hasUnread: ReadStates.hasUnread(channelId),
				manualHold,
				allUnreadVisible,
				parkedBeforeGesture,
				tailGapPending: this.host.isTailGapPending(),
			})
		) {
			ReadStateCommands.completeCatchUpFromBottom(channelId);
			return;
		}
		if (!settings.advanceLastReadOnScroll || !scrollingDown || manualHold) {
			return;
		}
		const messageId = resolveScrollAckMessageId({
			edge: settings.advanceLastReadScrollEdge,
			currentAckId: ackMessageId,
			scrollingDown,
			viewportTop: viewport.top,
			viewportBottom: viewport.bottom,
			messages: measureAll(),
			atLiveBottom: computeAtLiveBottom(scroller),
			hasMoreAfter: messages.hasMoreAfter,
			bannerVisible: shouldShowTopNewMessagesBar({
				inStream: true,
				unreadCount: ReadStates.getUnreadCount(channelId),
				hasOffScreenRemainingUnread: offScreenRemainingUnread,
				hideNewBannerOnCatchUp: settings.hideNewBannerOnCatchUp,
				bannerHiddenForVisit: settings.isBannerHiddenForVisit(channelId),
			}),
		});
		if (messageId != null) {
			ReadStateCommands.ackToMessageOnScroll(channelId, messageId);
		}
	}

	private tryHideBannerOnScrollStart(): void {
		const channelId = this.channelId;
		if (channelId == null) {
			return;
		}
		if (
			!shouldHideNewBannerOnScrollStart({
				enabled: UnreadCatchUpSettings.hideNewBannerOnCatchUp,
				alreadyHidden: UnreadCatchUpSettings.isBannerHiddenForVisit(channelId),
				manualHold: ReadStates.getIfExists(channelId)?.ackedManually ?? false,
				jumpActive: this.host.isJumpActive(),
				fromUserGesture: true,
			})
		) {
			return;
		}
		UnreadCatchUpSettings.hideBannerForVisit(channelId);
	}

	private tryClearOnVisibleBottomTrigger(trigger: VisibleBottomClearTrigger, parkedBeforeGesture = false): boolean {
		const channelId = this.channelId;
		if (channelId == null) {
			return false;
		}
		const settings = UnreadCatchUpSettings;
		const readState = ReadStates.getIfExists(channelId);
		const manualHold = readState?.ackedManually ?? false;
		const allUnreadVisible = isAllUnreadVisible({
			hasUnread: ReadStates.hasUnread(channelId),
			hasOffScreenRemainingUnread: this.measureRemainingUnread(),
		});
		if (
			shouldClearUnreadOnVisibleBottomTrigger({
				settledAtBottom: this.clocks.settledAtBottom,
				allUnreadVisible,
				hasUnread: ReadStates.hasUnread(channelId),
				manualHold,
				trigger,
				clearDividerAtBottom: settings.clearDividerAtBottom,
				clearOnIdleAtBottom: settings.clearOnIdleAtBottom,
				clearOnLeaveAtBottom: settings.clearOnLeaveAtBottom,
				visitDurationMs: trigger === 'leave_channel' ? UnreadCatchUpSettings.getVisitDurationMs(channelId) : undefined,
				minVisitDurationMs: trigger === 'leave_channel' ? CHANNEL_LEAVE_CLEAR_MIN_VISIT_MS : undefined,
				parkedBeforeGesture,
				scrollIntentAtBottom: trigger === 'scroll_intent' ? true : undefined,
				tailGapPending: this.host.isTailGapPending(),
			})
		) {
			ReadStateCommands.completeCatchUpFromBottom(channelId);
			return true;
		}
		return false;
	}

	private scheduleIdleClear(): void {
		if (!UnreadCatchUpSettings.clearOnIdleAtBottom || !Window.visible) {
			return;
		}
		const channelId = this.channelId;
		if (channelId == null) {
			return;
		}
		if (ReadStates.getIfExists(channelId)?.ackedManually) {
			return;
		}
		const allUnreadVisible = isAllUnreadVisible({
			hasUnread: ReadStates.hasUnread(channelId),
			hasOffScreenRemainingUnread: this.measureRemainingUnread(),
		});
		if (!this.clocks.settledAtBottom || !allUnreadVisible) {
			return;
		}
		this.resetIdleClear();
		this.idleClearTimer = window.setTimeout(() => {
			this.idleClearTimer = null;
			this.tryClearOnVisibleBottomTrigger('idle');
		}, VISIBLE_BOTTOM_IDLE_CLEAR_MS);
	}

	private resetIdleClear(): void {
		if (this.idleClearTimer != null) {
			clearTimeout(this.idleClearTimer);
			this.idleClearTimer = null;
		}
	}

	private measureRemainingUnread(): boolean {
		const channelId = this.channelId;
		const scroller = this.host.getScroller();
		const inner = this.host.getInner();
		if (channelId == null) {
			return false;
		}
		if (scroller == null || inner == null) {
			const remaining = ReadStates.hasUnread(channelId);
			this.host.onRemainingUnread(remaining);
			return remaining;
		}
		const hasUnread = ReadStates.hasUnread(channelId);
		const hasMoreAfter = MessagesState.getMessages(channelId).hasMoreAfter;
		const needsBounds = offScreenRemainingUnreadNeedsBounds({hasUnread, hasMoreAfter});
		const viewport = needsBounds ? scroller.getBoundingClientRect() : null;
		const remaining = hasOffScreenRemainingUnread({
			hasUnread,
			hasMoreAfter,
			unreadMessageBounds: needsBounds ? this.collectUnreadBounds(inner, channelId) : NO_MEASURED_MESSAGES,
			viewportTop: viewport?.top ?? 0,
			viewportBottom: viewport?.bottom ?? 0,
		});
		this.host.onRemainingUnread(remaining);
		return remaining;
	}

	private collectUnreadBounds(
		inner: HTMLElement,
		channelId: string,
		measured?: ReadonlyArray<MeasuredMessage>,
	): ReadonlyArray<MeasuredMessage> {
		const ackMessageId = ReadStates.ackMessageId(channelId);
		return (measured ?? measureMessageBounds(inner)).filter(
			(message) => ackMessageId == null || compareMessageIds(message.id, ackMessageId) > 0,
		);
	}

	private updateLastMessageTextVisibleForLeave(measured?: ReadonlyArray<MeasuredMessage>, viewport?: DOMRect): void {
		const channelId = this.channelId;
		const scroller = this.host.getScroller();
		const inner = this.host.getInner();
		if (channelId == null || scroller == null || inner == null) {
			return;
		}
		const hasMoreAfter = MessagesState.getMessages(channelId).hasMoreAfter;
		if (!lastMessageTextVisibleNeedsBounds({hasMoreAfter})) {
			this.unreadTextsVisibleForLeave = isLastMessageTextVisibleForLeave({
				lastMessageBounds: null,
				viewportTop: 0,
				viewportBottom: 0,
				hasMoreAfter,
			});
			return;
		}
		const viewportRect = viewport ?? scroller.getBoundingClientRect();
		const last = measured != null ? (measured[measured.length - 1] ?? null) : measureLastMessageBounds(inner);
		this.unreadTextsVisibleForLeave = isLastMessageTextVisibleForLeave({
			lastMessageBounds: last == null ? null : {top: last.top, bottom: last.bottom},
			viewportTop: viewportRect.top,
			viewportBottom: viewportRect.bottom,
			hasMoreAfter,
		});
	}
}
